import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Template from './template';
import { BtnContainer, BtnNext, FeatureRegister, SpaceDiv } from 'components';
import { useApiRequest } from 'hooks';
import {
  apiGetFeatureMaster,
  apiGetServiceFeature,
  apiServiceFeatureRegister,
} from 'api/ZimssaAPI';
import { BUTTON_COMPLETE } from 'commons/ZimssaString';
import { SERVICE_OFFER_STATUS } from 'commons/ZimssaConstant';
import { showModal } from 'commons/utils/showModal';

function FeatureRegisterView(): React.ReactElement {
  const history = useHistory();
  const { serviceType, serviceIdx } = useParams<{
    serviceType: string;
    serviceIdx: string;
  }>();
  const [fetchResult, fetchFeatureMaster] =
    useApiRequest<any>(apiGetFeatureMaster);
  const [featureMaster, setFeatureMaster] =
    useState<IFeatureMasterProps>(defaultProps);
  const [serviceFeature, setServiceFeature] = useState<
    Array<{ offerMasterId: number; status: string }>
  >([]);

  useEffect(() => {
    if (serviceIdx && serviceType) {
      apiGetServiceFeature(serviceType, Number(serviceIdx), 'all').then(
        (response) => {
          const { result, data } = response;
          if (result === 'success' && data) {
            const responseData: {
              isSubmittedOffer: boolean;
              offers: Array<{
                category: string;
                id: number;
                image: string;
                masterId: number;
                partnerServiceId: number;
                status: string;
                title: string;
              }>;
            } = data;
            let loadOffers: Array<{ offerMasterId: number; status: string }> =
              new Array<{ offerMasterId: number; status: string }>();
            if (responseData?.isSubmittedOffer) {
              if (responseData?.offers.length > 0) {
                loadOffers = featureMaster.offerMasterContentsList.map(
                  (item) => {
                    const responseOffer = responseData.offers.find(
                      (responseOffer) =>
                        responseOffer.masterId === item.offerMasterId
                    );

                    return {
                      offerMasterId: item.offerMasterId,
                      status: responseOffer
                        ? responseOffer.status
                        : SERVICE_OFFER_STATUS.NOT_AVAILABLE,
                    };
                  }
                );
              } else {
                loadOffers = featureMaster.offerMasterContentsList.map(
                  (item) => {
                    return {
                      offerMasterId: item.offerMasterId,
                      status: '',
                    };
                  }
                );
              }
            }
            setServiceFeature([...loadOffers]);
          }
        }
      );
    }
  }, [featureMaster, serviceIdx, serviceType]);

  useEffect(() => {
    if (serviceType) {
      fetchFeatureMaster({
        type: 'REQUEST',
        body: [serviceType],
      });
    }
  }, [fetchFeatureMaster, serviceType]);

  useEffect(() => {
    const { result, data } = fetchResult;
    if (result === 'success' && data) {
      const responseData: IFeatureMasterProps = data;
      const { offerMasterContentsList } = responseData;

      const categories: string[] = [];
      offerMasterContentsList.forEach((item) => {
        if (!categories.includes(item.offerCategory)) {
          categories.push(item.offerCategory);
        }
      });

      setFeatureMaster({
        offerMasterContentsList,
        offerMasterCategoryList: categories,
      });
    }
  }, [fetchResult]);

  return (
    <Template title={'제공 서비스 등록하기'}>
      {featureMaster.offerMasterCategoryList.map((category, index) => (
        <FeatureRegister
          key={index}
          serviceType={serviceType}
          category={category}
          contentsList={featureMaster.offerMasterContentsList
            .filter((item) => item.offerCategory === category)
            .map((contents) => ({
              name: contents.offerTitle,
              defaultCheck:
                serviceFeature.find(
                  (item) => item.offerMasterId === contents.offerMasterId
                )?.status === SERVICE_OFFER_STATUS.AVAILABLE,
              notDefaultCheck:
                serviceFeature.find(
                  (item) => item.offerMasterId === contents.offerMasterId
                )?.status === SERVICE_OFFER_STATUS.NEGOTIABLE,
              notServiceCheck:
                serviceFeature.find(
                  (item) => item.offerMasterId === contents.offerMasterId
                )?.status === SERVICE_OFFER_STATUS.NOT_AVAILABLE,
              onClick: (
                e: React.MouseEvent<HTMLDivElement, MouseEvent>,
                value: string,
                checked?: boolean
              ) => {
                setServiceFeature([
                  ...serviceFeature.filter(
                    (item) => item.offerMasterId !== contents.offerMasterId
                  ),
                  {
                    offerMasterId: contents.offerMasterId,
                    status: value,
                  },
                ]);
              },
            }))}
        />
      ))}
      <SpaceDiv height={36} />
      <BtnContainer>
        <BtnNext
          {...{
            contents: BUTTON_COMPLETE,
            isActive:
              serviceFeature.length ===
              featureMaster.offerMasterContentsList.length,
            onClick: useCallback(
              async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                if (
                  serviceFeature.length !==
                  featureMaster.offerMasterContentsList.length
                ) {
                  const result = await showModal({
                    title:
                      '선택 된 제공 서비스가 없습니다. 이대로 진행 하시겠습니까?',
                  });

                  if (result) {
                    history.goBack();
                  }
                } else {
                  const result = await showModal({
                    title: '선택 된 제공 서비스를 등록 하시겠습니까?',
                  });

                  if (result) {
                    apiServiceFeatureRegister({
                      partnerServiceId: serviceIdx ? Number(serviceIdx) : 0,
                      serviceType: serviceType ? serviceType : '',
                      partnerServiceOffers: serviceFeature.map((item) => ({
                        offerMasterId: item.offerMasterId,
                        status: item.status,
                      })),
                    }).then((response) => {
                      const { result } = response;
                      if (result === 'success') {
                        alert('제공 서비스 등록을 완료 하였습니다.');
                        history.goBack();
                      }
                    });
                  }
                }
              },
              [serviceFeature, history, serviceIdx, serviceType]
            ),
          }}
        />
      </BtnContainer>
    </Template>
  );
}

export default FeatureRegisterView;

interface IFeatureMasterProps {
  offerMasterCategoryList: Array<string>;
  offerMasterContentsList: Array<{
    offerMasterId: number;
    offerCategory: string;
    offerTitle: string;
  }>;
}

const defaultProps: IFeatureMasterProps = {
  offerMasterCategoryList: [],
  offerMasterContentsList: [],
};
